"use client";

import React from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { 
  Zap, 
  LayoutDashboard, 
  ShieldCheck, 
  User, 
  ChevronRight,
  LogOut,
  History
} from "lucide-react";
import { ThemeToggle } from "@/components/ThemeToggle";
import AppLogo from "@/components/ui/AppLogo";
import { clearTokens } from "@/lib/api";

export default function GlobalNavbar() {
  const pathname = usePathname();
  const isAuthPage = pathname?.includes("sign-up-login-screen");
  
  if (isAuthPage) return null;

  const isAdmin = pathname?.startsWith("/admin");
  const isUser = pathname?.startsWith("/user-dashboard") || pathname?.startsWith("/request-history");
  const isDashboard = isAdmin || isUser;

  return (
    <nav className="fixed top-0 left-0 right-0 z-[60] border-b border-zinc-200 dark:border-zinc-800/40 bg-white/80 dark:bg-zinc-950/80 backdrop-blur-md transition-all duration-300">
      <div className={`mx-auto px-4 h-14 flex items-center justify-between ${isDashboard ? 'max-w-full' : 'max-w-7xl'}`}>
        <div className="flex items-center gap-6">
          <Link href="/" className="flex items-center gap-3 group">
            <div className="relative">
              <div className="absolute -inset-1.5 bg-blue-600/20 dark:bg-blue-600/20 rounded-full blur-sm opacity-0 group-hover:opacity-100 transition-opacity"></div>
              <AppLogo size={32} className="relative transform group-hover:scale-105 transition-transform duration-300" />
            </div>
            <div className="flex flex-col">
              <span className="text-sm font-black tracking-tight text-zinc-900 dark:text-white leading-tight uppercase">TeraBox API</span>
              {isAdmin && <span className="text-[9px] font-black text-amber-600 dark:text-amber-400 uppercase tracking-[0.2em] leading-none">Admin Panel</span>}
              {isUser && <span className="text-[9px] font-black text-blue-600 dark:text-blue-400 uppercase tracking-[0.2em] leading-none">Developer Console</span>}
            </div>
          </Link>

          {!isDashboard && (
            <div className="hidden md:flex items-center gap-6 ml-4">
              <Link href="/features" className="text-[10px] font-black text-zinc-500 dark:text-zinc-400 hover:text-blue-600 dark:hover:text-white transition-colors uppercase tracking-[0.15em]">Features</Link>
              <Link href="/pricing" className="text-[10px] font-black text-zinc-500 dark:text-zinc-400 hover:text-blue-600 dark:hover:text-white transition-colors uppercase tracking-[0.15em]">Pricing</Link>
              <Link href="/docs" className="text-[10px] font-black text-zinc-500 dark:text-zinc-400 hover:text-blue-600 dark:hover:text-white transition-colors uppercase tracking-[0.15em]">Docs</Link>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 sm:gap-4">
          <div className="flex items-center gap-1 sm:gap-2 mr-2">
            <ThemeToggle />
          </div>

          {!isDashboard ? (
            <div className="flex items-center gap-3">
              <Link href="/sign-up-login-screen" className="hidden sm:block text-[10px] font-black text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white transition-colors uppercase tracking-widest px-2">
                Login
              </Link>
              <Link href="/sign-up-login-screen">
                <button className="px-4 py-2 text-[10px] font-black bg-blue-600 text-white dark:bg-white dark:text-zinc-950 rounded-lg hover:bg-blue-700 dark:hover:bg-zinc-100 transition-all uppercase tracking-widest shadow-md">
                  Get Started
                </button>
              </Link>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              {isUser && (
                <Link href="/request-history" className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-zinc-100 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 text-[10px] font-black text-zinc-600 dark:text-zinc-400 uppercase tracking-widest hover:bg-zinc-200 dark:hover:bg-zinc-800 transition-all">
                  <History size={12} />
                  Logs
                </Link>
              )}
              {isAdmin && (
                <Link href="/admin/logs" className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-zinc-100 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 text-[10px] font-black text-zinc-600 dark:text-zinc-400 uppercase tracking-widest hover:bg-zinc-200 dark:hover:bg-zinc-800 transition-all">
                  <ShieldCheck size={12} />
                  Admin Logs
                </Link>
              )}
              <div className="h-6 w-px bg-zinc-200 dark:bg-zinc-800 mx-1 hidden sm:block"></div>
              <button 
                onClick={() => {
                  clearTokens();
                  window.location.href = "/sign-up-login-screen";
                }}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 transition-all text-[10px] font-black uppercase tracking-widest"
              >
                <LogOut size={12} />
                <span className="hidden sm:inline">Exit</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}
